import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import t from "@/lang/lang";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CommonDialogFooter } from "@/shared/components/dialog/dialog-common";
import {
  useBusinessCentralOutboxGesecInvoiceSendMutation,
  useBusinessCentralOutboxGesecInvoicesShowQuery,
} from "@/services/backend/business-central/api/outbox-gesec-invoice-api";
import { OutboxStatus } from "../types/outbox";

export function OutboxGesecInvoiceSendDialog({
  outboxId,
}: {
  outboxId: string;
}) {
  const { data } = useBusinessCentralOutboxGesecInvoicesShowQuery({ outboxId });
  const [open, setOpen] = useState(false);
  const [post, { isLoading, error, isSuccess, reset }] =
    useBusinessCentralOutboxGesecInvoiceSendMutation();

  useEffect(() => {
    if (isSuccess) {
      setOpen(false);
      toast.success(t("Die Rechnungsdaten wurden erfolgreich übermittelt."));
      reset();
    }
  }, [isSuccess, reset]);

  const doSend = () => {
    if (isLoading) return;
    post({ outboxId });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          disabled={!data || data.outboxStatus === OutboxStatus.Sent}
        >
          {t("Übermitteln")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Rechnungsdaten übermitteln?")}</DialogTitle>
          <DialogDescription>
            {t("Übermittlung der Daten an Business Central.")}
          </DialogDescription>
        </DialogHeader>
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>{t("Nicht widerrufbar")}</AlertTitle>
          <AlertDescription>
            {t(
              "Wenn die Daten erfolgreich übermittelt wurden, können sie hier nicht mehr geändert werden. Änderungen sind dann nur noch in Business Central möglich",
            )}
          </AlertDescription>
        </Alert>
        <RTKQueryErrorAlert error={error} showValidationErrors />
        <CommonDialogFooter isLoading={isLoading} onClickAction={doSend} />
      </DialogContent>
    </Dialog>
  );
}
